import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { FlaskConical, CreditCard, QrCode, Wallet, ArrowLeft, UserPlus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const SimulateBox = ({ editorRef }) => {
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [err, setErr] = useState('');
  const simulate = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/digital/${editorRef}/trial/simulate-subscriber`);
      setList((l) => [data, ...l]);
    } catch (e) {
      setErr(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Não foi possível simular agora.');
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="trial-simulate-box" style={{ marginTop: '1rem', padding: '1rem', borderRadius: 12, background: '#f0fdfa', border: '1px solid #99f6e4' }}>
      <p style={{ color: '#134e4a', fontSize: '0.92rem', marginBottom: '0.75rem' }}>
        Quer ver como funciona? Simule a entrada de um novo assinante: ela aparece na hora no menu "Financeiro" do painel do editor, marcada como simulação (não gera cobrança nem repasse).
      </p>
      <button type="button" onClick={simulate} disabled={busy} data-testid="trial-simulate-btn"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f59e0b', color: 'white', border: 'none', borderRadius: 999, padding: '0.6rem 1.3rem', fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Simular novo assinante
      </button>
      {err && <p data-testid="trial-simulate-error" style={{ color: '#c53030', marginTop: 8, fontSize: '0.85rem' }}>{err}</p>}
      {list.map((s) => (
        <div key={s.id} data-testid="trial-simulated-entry" style={{ marginTop: 10, background: 'white', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.85rem', color: '#2d3748', border: '1px solid #e2e8f0' }}>
          <strong>{s.nome}</strong> · {new Date(s.paid_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}<br />
          Assinatura: {BRL(s.amount)} · Livrum ({s.platform_pct}%): {BRL(s.platform_fee)} · Editor recebe: <strong>{BRL(s.editor_amount)}</strong> · Simulação: não entra em repasse
        </div>
      ))}
    </div>
  );
};

const Item = ({ icon: Icon, title, children, testId }) => (
  <div data-testid={testId} style={{ display: 'flex', gap: 14, padding: '1rem 0', borderTop: '1px solid #edf2f7' }}>
    <Icon className="w-6 h-6 flex-shrink-0" style={{ color: '#0d9488', marginTop: 2 }} />
    <div>
      <p style={{ fontWeight: 700, color: '#1a202c', marginBottom: 4 }}>{title}</p>
      <p style={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: 1.6 }}>{children}</p>
    </div>
  </div>
);

// Shown instead of the subscribe page while the publisher is on the free trial
export default function TrialModePage() {
  const { editorId } = useParams();
  const navigate = useNavigate();
  return (
    <div data-testid="trial-mode-page" style={{ minHeight: '100vh', background: '#f0fdfa', padding: '2.5rem 1.25rem' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', background: 'white', borderRadius: 16, padding: '2rem', boxShadow: '0 12px 32px rgba(13,148,136,0.1)' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '0.35rem 0.9rem', fontWeight: 600, fontSize: '0.85rem', marginBottom: '1rem' }}>
          <FlaskConical className="w-4 h-4" /> Modo teste
        </div>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a202c', marginBottom: '0.75rem' }}>A assinatura está desabilitada no modo teste</h1>
        <p style={{ color: '#4a5568', lineHeight: 1.6, marginBottom: '0.5rem' }}>
          Esta é a página de cadastro de novos assinantes, com a cobrança das assinaturas. Durante o teste gratuito ela fica desativada;
          para navegar no catálogo como leitor, entre com o leitor de teste mostrado no painel do editor.
        </p>
        <Item icon={CreditCard} title="Assinatura mensal no cartão" testId="trial-info-card">
          O leitor cadastra o cartão de crédito e a mensalidade é cobrada automaticamente todo mês (cobrança recorrente). Ele pode cancelar quando quiser.
        </Item>
        <Item icon={QrCode} title="Assinatura mensal no Pix" testId="trial-info-pix">
          A assinatura também é mensal e segue até o leitor cancelar: antes de cada vencimento ele recebe por e-mail o Pix do mês seguinte (avisos 5 dias e 1 dia antes).
        </Item>
        <Item icon={Wallet} title="Divisão dos valores e Financeiro" testId="trial-info-split">
          Do valor da assinatura mensal definido pelo editor, <strong>30% fica com a plataforma Livrum e 70% com o editor</strong>. No menu
          <strong> "Financeiro"</strong> do painel administrativo, o editor acompanha em tempo real todas as entradas de assinantes, os valores
          mensais a receber da Livrum na conta que ele definir e as regras de repasse dos valores.
        </Item>
        <SimulateBox editorRef={editorId} />
        <button type="button" onClick={() => navigate(`/${editorId}`)} data-testid="trial-mode-back-btn"
          style={{ marginTop: '1.25rem', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0d9488', color: 'white', border: 'none', borderRadius: 999, padding: '0.65rem 1.4rem', fontWeight: 600, cursor: 'pointer' }}>
          <ArrowLeft className="w-4 h-4" /> Voltar ao catálogo
        </button>
      </div>
    </div>
  );
}
